import React, { useEffect, useState } from 'react';
import type { usePresentationRemoteController } from '../../hooks/usePresentationRemoteController';
import type { RemoteTtsSettings } from '../../types/presentationRemote';
import { RemoteFlatIcon } from './RemoteFlatIcons';

type Ctl = ReturnType<typeof usePresentationRemoteController>;

const DEFAULT_DRAFT: RemoteTtsSettings = { voiceURI: null, rate: 0.95, pitch: 1, volume: 1 };

/** 읽어주기 설정 — 음성 목록은 펼칠 때만 요청 (presence에 싣지 않음) */
const RemoteTtsSettingsCard: React.FC<{ ctl: Ctl; disabled: boolean; busy: boolean; embedded?: boolean }> = ({
  ctl,
  disabled,
  busy,
  embedded = false,
}) => {
  /** embedded: 설정 시트 안에서 항상 펼친 상태로 사용 */
  const [open, setOpen] = useState(embedded);
  const [draft, setDraft] = useState<RemoteTtsSettings>(DEFAULT_DRAFT);
  const tts = ctl.tts;
  const target = ctl.selectedSessionId;
  const { requestTtsVoices } = ctl;

  /** 대상별 1회 자동 요청 — 실패 시 무한 재요청하지 않고 '다시 시도' 버튼 제공 */
  const [load, setLoad] = useState<{ target: string; state: 'loading' | 'failed'; error?: string } | null>(null);
  const loadVoices = React.useCallback(
    async (includeAll: boolean) => {
      if (!target) return;
      setLoad({ target, state: 'loading' });
      const ok = await requestTtsVoices(includeAll);
      setLoad((cur) => (cur?.target !== target ? cur : ok ? null : { target, state: 'failed' }));
    },
    [target, requestTtsVoices]
  );
  useEffect(() => {
    if (!open || disabled || tts || !target) return;
    if (load?.target === target) return;
    void loadVoices(false);
  }, [open, disabled, tts, target, load, loadVoices]);

  const lastReq = ctl.log.find((e) => e.kind === 'tts' && e.label === '음성 목록 요청');
  const failReason =
    lastReq?.error === 'not_implemented'
      ? '프레젠테이션 탭이 이전 버전이라 음성 목록을 지원하지 않아요. 프레젠테이션 탭을 새로고침해 주세요.'
      : lastReq?.error === 'timeout'
        ? '프레젠테이션이 응답하지 않아요. 탭이 열려 있는지 확인하고 다시 시도해 주세요.'
        : lastReq?.error === 'tts_unavailable'
          ? '이 프레젠테이션 기기는 읽어주기(음성 합성)를 지원하지 않아요.'
          : '음성 목록을 불러오지 못했어요.';
  const failed = !tts && load?.target === target && load.state === 'failed';

  const settingsKey = tts ? JSON.stringify(tts.settings) : '';
  useEffect(() => {
    if (tts?.settings) setDraft(tts.settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsKey]);

  const savedMissing = !!draft.voiceURI && !!tts && !tts.voices.some((v) => v.voiceURI === draft.voiceURI);
  const off = disabled || busy;

  const slider = (key: 'rate' | 'pitch' | 'volume', label: string, min: number, max: number) => (
    <label className="cp-remote-slider">
      <span>
        {label} <b>{draft[key].toFixed(2)}</b>
      </span>
      <input
        type="range"
        style={{ ['--cp-rt-fill' as string]: `${((draft[key] - min) / (max - min)) * 100}%` } as React.CSSProperties}
        min={min}
        max={max}
        step={0.05}
        value={draft[key]}
        disabled={off}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: Number(e.target.value) }))}
      />
    </label>
  );

  const content = (
    <>
      {disabled ? (
        <p className="cp-remote-hint">프레젠테이션을 선택하면 그 기기의 음성을 고를 수 있어요.</p>
      ) : failed ? (
        <div className="cp-remote-tts-fail" role="alert">
          <p className="cp-remote-cmd-status is-fail">{failReason}</p>
          <button type="button" className="cp-remote-btn" disabled={busy} onClick={() => void loadVoices(false)}>
            다시 시도
          </button>
        </div>
      ) : !tts ? (
        <p className="cp-remote-hint">음성 목록을 불러오는 중…</p>
      ) : (
        <>
          <p className="cp-remote-hint">
            프레젠 기기 음성 {tts.total}개 중 한국어 {tts.koCount}개
            {tts.voices.length < (tts.includeAll ? tts.total : tts.koCount) ? ` (최대 ${tts.voices.length}개 표시)` : ''}
          </p>
          <select
            className="cp-remote-input"
            aria-label="음성"
            value={draft.voiceURI ?? ''}
            disabled={off}
            onChange={(e) => setDraft((d) => ({ ...d, voiceURI: e.target.value || null }))}
          >
            <option value="">기본 한국어 음성 (자동)</option>
            {savedMissing ? <option value={draft.voiceURI ?? ''}>저장된 음성 (이 기기에 없음 → 기본 사용)</option> : null}
            {tts.voices.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>
                {v.name} · {v.lang} · {v.localService ? '로컬' : '온라인'}
                {v.default ? ' · 기본' : ''}
              </option>
            ))}
          </select>
          <label className="cp-remote-check">
            <input
              type="checkbox"
              checked={tts.includeAll}
              disabled={off}
              onChange={(e) => void loadVoices(e.target.checked)}
            />
            모든 언어 음성 보기
          </label>
          {slider('rate', '속도', 0.5, 1.5)}
          {slider('pitch', '높낮이', 0.5, 1.5)}
          {slider('volume', '음량', 0, 1)}
          <div className="cp-remote-row cp-rc-tts-actions">
            <button
              type="button"
              className="cp-remote-btn"
              disabled={off}
              onClick={async () => {
                if (await ctl.setTtsSettings(draft)) void ctl.previewTts();
              }}
            >
              <RemoteFlatIcon kind="play" />미리듣기
            </button>
            <button type="button" className="cp-remote-btn" disabled={off} onClick={() => setDraft(DEFAULT_DRAFT)}>
              기본값
            </button>
            <button type="button" className="cp-remote-btn cp-remote-btn--primary" disabled={off} onClick={() => void ctl.setTtsSettings(draft)}>
              적용
            </button>
          </div>
        </>
      )}
    </>
  );
  if (embedded) return <div className="cp-rc-tts">{content}</div>;
  return (
    <details className="cp-remote-card cp-remote-tts" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary className="cp-remote-section-title cp-remote-tts-summary"><RemoteFlatIcon kind="speaker" />읽어주기 설정</summary>
      {content}
    </details>
  );
};

export default RemoteTtsSettingsCard;
